import React, { useState, useRef, useEffect } from 'react';
import { Search } from 'lucide-react';

interface SerpApiResult {
  title: string;
  address?: string;
  type?: string;
  place_id?: string;
  gps_coordinates?: {
    latitude: number;
    longitude: number;
  };
  phone?: string;
  hours?: string;
  links?: {
    website?: string;
  };
}

interface SerpApiResponse {
  local_results?: SerpApiResult[];
  error?: string;
}

interface StudentAccommodationSearchInputProps {
  onSelectAccommodation: (
    accommodationName: string, 
    placeId?: string,
    latitude?: number,
    longitude?: number,
    address?: string,
    website?: string,
    phone?: string,
    openHours?: string
  ) => void;
  initialValue?: string;
  className?: string;
  required?: boolean;
}

export default function StudentAccommodationSearchInput({ 
  onSelectAccommodation, 
  initialValue = '',
  className = '',
  required = false
}: StudentAccommodationSearchInputProps) {
  const [query, setQuery] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<SerpApiResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSelectedFromResults, setHasSelectedFromResults] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const searchTimeout = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchAccommodation = async (searchQuery: string) => {
    if (searchQuery.length < 4) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase URL not configured');
      }

      const apiUrl = `${supabaseUrl}/functions/v1/search-companies?q=${encodeURIComponent(searchQuery.trim())}`;
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data: SerpApiResponse = await response.json();
      
      if (data.error) {
        // Check for specific "no results" error
        if (data.error === "Google Local hasn't returned any results for this query.") {
          setError("Sorry we can't find any results for this search, try to just search a keyword and place e.g. Unite Students London");
          setSuggestions([]);
          setShowSuggestions(false);
          return;
        } else {
          setError(data.error);
          setSuggestions([]);
          setShowSuggestions(false);
          return;
        }
      }
      
      if (data.local_results && Array.isArray(data.local_results)) {
        setSuggestions(data.local_results);
        setShowSuggestions(data.local_results.length > 0);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
      
    } catch (err) {
      console.error('Student accommodation search failed:', err);
      setError('Student accommodation search failed. Please try again.');
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setError(null);
    setHasSelectedFromResults(false); // Reset when user types manually
    
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Wait 1 second before making API call, and only if 4+ characters
    searchTimeout.current = setTimeout(() => {
      searchAccommodation(value);
    }, 1000);
  };

  const handleSelectAccommodation = (accommodation: SerpApiResult) => {
    const accommodationName = accommodation.title;
    const placeId = accommodation.place_id;
    const latitude = accommodation.gps_coordinates?.latitude;
    const longitude = accommodation.gps_coordinates?.longitude;
    const address = accommodation.address;
    const website = accommodation.links?.website;
    const phone = accommodation.phone;
    const openHours = accommodation.hours;
    
    setQuery(accommodationName);
    setShowSuggestions(false);
    setHasSelectedFromResults(true); // Mark that user selected from results
    onSelectAccommodation(accommodationName, placeId, latitude, longitude, address, website, phone, openHours);
  };

  return (
    <div className={`relative w-full ${className}`}>
      <input
        type="text"
        value={query}
        onChange={handleQueryChange}
        placeholder="Enter company name and location"
        className="w-full p-3 pr-12 rounded-xl bg-white text-gray-900 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#30a86b] focus:border-transparent placeholder:text-[10px]"
        required={required}
      />
      <button 
        onClick={() => searchAccommodation(query)}
        className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 bg-[#30a86b] rounded-md hover:bg-[#2a9760] transition-colors"
        disabled={isLoading || query.length < 4}
      >
        <Search className="w-5 h-5 text-white animate-pulse-search" />
      </button>
      
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((accommodation, index) => (
            <div
              key={index}
              className="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
              onClick={() => handleSelectAccommodation(accommodation)}
            >
              <div className="font-medium text-gray-900">
                {accommodation.title}
              </div>
              {accommodation.address && (
                <div className="text-sm text-gray-500">
                  {accommodation.address}
                </div>
              )}
              {accommodation.phone && (
                <div className="text-sm text-gray-500">
                  📞 {accommodation.phone}
                </div>
              )}
              {accommodation.hours && (
                <div className="text-sm text-gray-500">
                  🕒 {accommodation.hours}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {isLoading && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3">
          <div className="text-gray-600 text-center flex items-center justify-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#30a86b]"></div>
            Searching companies...
          </div>
        </div>
      )}
      
      {error && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-red-300 rounded-lg shadow-lg p-3">
          <div className="text-red-600 text-center text-sm">{error}</div>
        </div>
      )}
      
      {query.length > 0 && query.length < 4 && !hasSelectedFromResults && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3">
          <div className="text-gray-500 text-center text-[10px]">
            Enter student accommodation provider name and location
          </div>
        </div>
      )}
    </div>
  );
}